import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { api } from "../../lib/api";
import { Eye, FileDown, QrCode, Mail, Share2, ArrowUpRight } from "lucide-react";

/**
 * "Your media kit was viewed X times this week" — athlete dashboard widget.
 *
 * Fed by GET /api/dashboard/kit-views which excludes bot traffic. Renders a
 * 7-day spark, a per-ref breakdown and a soft empty-state ("Share your kit
 * to start tracking") that links to the media-kit page.
 */
const REF_META = {
  pdf: { label: "PDF views", Icon: FileDown },
  direct: { label: "Link visits", Icon: Eye },
  qr: { label: "QR scans", Icon: QrCode },
  outreach_email: { label: "From email", Icon: Mail },
  social: { label: "From social", Icon: Share2 },
  other: { label: "Other", Icon: Eye },
};


function fmtRelative(iso) {
  if (!iso) return "";
  try {
    const then = new Date(iso); const now = new Date();
    const diffSec = Math.max(0, Math.round((now - then) / 1000));
    if (diffSec < 60) return "just now";
    if (diffSec < 3600) return `${Math.round(diffSec / 60)}m ago`;
    if (diffSec < 86400) return `${Math.round(diffSec / 3600)}h ago`;
    return `${Math.round(diffSec / 86400)}d ago`;
  } catch { return ""; }
}


export default function KitViewsCard() {
  const [data, setData] = useState(null);
  const [loaded, setLoaded] = useState(false);
  useEffect(() => {
    api.get("/dashboard/kit-views")
      .then(({ data }) => setData(data))
      .catch(() => setData(null))
      .finally(() => setLoaded(true));
  }, []);

  if (!loaded) {
    return (
      <div className="surface p-6 animate-pulse" data-testid="kit-views-card-loading">
        <div className="h-3 w-24 bg-line mb-3" />
        <div className="h-8 w-32 bg-line" />
      </div>
    );
  }
  if (!data) return null;

  // Empty state — no share link enabled yet.
  if (!data.share_enabled) {
    return (
      <div className="surface p-6" data-testid="kit-views-empty-noshare">
        <div className="flex items-center justify-between mb-3">
          <div className="label-eyebrow">Media kit reach</div>
          <Eye className="w-4 h-4 text-ink-muted" />
        </div>
        <h3 className="font-heading text-xl uppercase tracking-tight">Track who's viewing your kit.</h3>
        <p className="text-sm text-ink-muted mt-2 leading-relaxed">
          Enable your public share link to see how many brands open your media kit, where they came from, and when.
        </p>
        <Link to="/app/media-kit"
          className="btn-volt inline-flex items-center gap-2 mt-5 text-xs"
          data-testid="kit-views-enable-cta">
          Enable share link <ArrowUpRight className="w-3.5 h-3.5" />
        </Link>
      </div>
    );
  }

  // Soft empty — share enabled but no views yet.
  if (data.total === 0) {
    return (
      <div className="surface p-6" data-testid="kit-views-empty-zero">
        <div className="flex items-center justify-between mb-3">
          <div className="label-eyebrow">Media kit reach</div>
          <Eye className="w-4 h-4 text-ink-muted" />
        </div>
        <h3 className="font-heading text-xl uppercase tracking-tight">No views yet — but you're set up.</h3>
        <p className="text-sm text-ink-muted mt-2 leading-relaxed">
          Your share link is live. Send it via outreach or attach the PDF in your next pitch — we'll start tracking who's reading it.
        </p>
      </div>
    );
  }

  // Sparkline scaling
  const spark = data.spark || Array(7).fill(0);
  const maxV = Math.max(...spark, 1);

  return (
    <div className="surface p-6" data-testid="kit-views-card">
      <div className="flex items-center justify-between mb-4">
        <div className="label-eyebrow">Media kit reach · last 30 days</div>
        <Eye className="w-4 h-4 text-volt" />
      </div>
      <div className="flex items-baseline gap-3 flex-wrap">
        <div className="font-heading text-4xl md:text-5xl uppercase" data-testid="kit-views-week">
          {data.week}
        </div>
        <div className="text-xs text-ink-muted uppercase tracking-widest">
          views this week{data.today ? ` · ${data.today} today` : ""}
        </div>
      </div>

      {/* 7-day spark — pure CSS bars */}
      <div className="flex items-end gap-1 mt-5 h-12" data-testid="kit-views-spark">
        {spark.map((v, i) => (
          <div key={i} className="flex-1 bg-volt/30 hover:bg-volt/60 transition-colors"
               style={{ height: `${Math.max((v / maxV) * 100, 4)}%` }}
               title={`${v} view${v === 1 ? "" : "s"}`} />
        ))}
      </div>
      <div className="flex justify-between text-[9px] text-ink-dim mt-1 uppercase tracking-widest">
        <span>7 days ago</span><span>Today</span>
      </div>

      {/* Per-ref breakdown */}
      <div className="grid grid-cols-2 gap-2 mt-5">
        {Object.entries(data.by_ref || {})
          .sort((a, b) => b[1] - a[1])
          .slice(0, 4)
          .map(([ref, count]) => {
            const meta = REF_META[ref] || REF_META.other;
            const Icon = meta.Icon;
            return (
              <div key={ref} className="border border-line px-3 py-2 flex items-center gap-2"
                   data-testid={`kit-views-ref-${ref}`}>
                <Icon className="w-3.5 h-3.5 text-volt flex-shrink-0" />
                <div className="min-w-0">
                  <div className="font-heading text-base">{count}</div>
                  <div className="text-[9px] text-ink-muted uppercase tracking-widest truncate">{meta.label}</div>
                </div>
              </div>
            );
          })}
      </div>

      {/* Last view */}
      {data.last_view && (
        <div className="mt-5 pt-4 border-t border-line text-xs text-ink-muted" data-testid="kit-views-last">
          Last viewed <span className="text-white">{fmtRelative(data.last_view.viewed_at)}</span>
          {data.last_view.ref && data.last_view.ref !== "other" && (
            <> via <span className="text-volt">{(REF_META[data.last_view.ref] || REF_META.other).label.toLowerCase()}</span></>
          )}.
        </div>
      )}

      <div className="mt-5">
        <Link to="/app/media-kit"
          className="text-xs uppercase tracking-widest text-volt inline-flex items-center gap-1.5 hover:text-white"
          data-testid="kit-views-manage-link">
          Manage media kit <ArrowUpRight className="w-3.5 h-3.5" />
        </Link>
      </div>
    </div>
  );
}
